import { AppLocation } from 'src/navigation/appLocations'
import {
  virtualCardsYourCardsRouteProps,
  yourCardsActiveRouteProps,
  yourCardsInactiveRouteProps,
} from 'src/navigation/routeProps'
import * as Routes from 'src/navigation/urls'

/**
 * Virtual Cards flat route tree — single source for the family tabs, mirrors
 * budgetsRouteTree. See src/navigation/AGENTS.md for the differences from Budgets.
 */

export interface VirtualCardsFamilyTabNode {
  /** Stable key used for active-tab detection and FamilyLayout. */
  key: string
  /** Translation key; rendered with <Trns/> in the page. */
  labelSource: string
  /** AppLocation that drives sidenav active state via locationHooks. */
  appLocation: AppLocation
  /** Exact URL pattern, e.g. `/virtualcards/allcards`. */
  path: string
}

export interface VirtualCardsFamilyAliasNode {
  /** AppLocation the alias resolves to; must be non-undefined so it out-specifies the entity :id. */
  appLocation: AppLocation
  /** Exact URL pattern, e.g. `/virtualcards/requests`. */
  path: string
}

export const virtualCardsRouteTree = {
  family: {
    /** `/virtualcards` — redirects to the first available family route. */
    rootPath: Routes.virtualCardsUrl(),
    rootAppLocation: AppLocation.VirtualCards,
    /**
     * Legacy parity: redirects to All Cards if the user has access, else to My
     * Wallet's virtual cards (data-dependent; page owns the choice).
     */
    redirectPrimaryTo: Routes.virtualCardsAllCardsUrl(),
    redirectFallbackTo: Routes.myWalletVirtualCardsUrl(),
    tabs: [
      {
        key: 'allcards',
        labelSource: 'All Cards',
        appLocation: AppLocation.VirtualCardsAll,
        path: Routes.virtualCardsAllCardsUrl(),
      },
      {
        key: 'needsapproval',
        labelSource: 'Needs Approval',
        appLocation: AppLocation.VirtualCardsNeedsApproval,
        path: Routes.virtualCardsNeedsApprovalUrl(),
      },
    ] as VirtualCardsFamilyTabNode[],
    /**
     * `/virtualcards/requests` aliases Needs Approval; maps to AppLocation.Home
     * (legacy parity) and must carry a real id to out-specify the entity :id.
     */
    aliasPaths: [
      {
        appLocation: AppLocation.Home,
        path: Routes.virtualCardsRequestsUrl(),
      },
    ] as VirtualCardsFamilyAliasNode[],
    /**
     * Vestigial paths the legacy :id route exempted; render-only (no metadata),
     * so locationHooks still resolves them via /virtualcards/:id. Shell decision:
     * they render the family not-found WITHOUT the tab bar, as legacy did.
     */
    notFoundChildPaths: [
      yourCardsActiveRouteProps.path,
      yourCardsInactiveRouteProps.path,
      virtualCardsYourCardsRouteProps.path,
    ],
  },
  entity: {
    /** `/virtualcards/:id` — single detail route, no sub-tabs. */
    rootPath: Routes.virtualCardDetailUrl(':id'),
    rootAppLocation: AppLocation.VirtualCardDetail,
  },
}

/**
 * Family GATE paths (root + tabs + aliases + vestigial children), enumerated so
 * each static path out-ranks the entity /virtualcards/:id (registered first).
 */
export const virtualCardsFamilyGatePaths: string[] = [
  virtualCardsRouteTree.family.rootPath,
  ...virtualCardsRouteTree.family.tabs.map((t) => t.path),
  ...virtualCardsRouteTree.family.aliasPaths.map((a) => a.path),
  ...virtualCardsRouteTree.family.notFoundChildPaths,
]

/** Entity GATE path — single :id route, no sub-tabs (unlike Budgets/Accounts). */
export const virtualCardDetailGatePath: string =
  virtualCardsRouteTree.entity.rootPath
